import { BatchSummary } from "@bcc/components/batches/batch-summary";
import { BatchesTable } from "@bcc/components/batches/batches-table";
import { CommonHeader } from "@bcc/components/common-header";
import { BatchActions } from "@bcc/components/orders/batch-actions";
import { OrdersStats } from "@bcc/components/orders/orders-stats";
import { OrdersTable } from "@bcc/components/orders/orders-table";
import { Button } from "@bcc/components/ui/button";
import { Input } from "@bcc/components/ui/input";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@bcc/components/ui/tabs";
import { useRoasterProfile } from "@bcc/contexts/roaster-profile-context";
import { Download, Filter, Plus, Search } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { setOrdersViewTestId } from "./orders-view.utils";

interface OrderData {
  id: string;
  customer: string;
  product: string;
  quantity: number;
  weight: number;
  amount: string;
  status: "completed" | "waiting" | "shipped" | "cancelled";
  date: string;
  age: string;
  marketplace: string;
  tracking: string | null;
  batch: string | null;
  carrier: "USPS" | "UPS";
}

interface BatchData {
  id: string;
  createdAt: string;
  status: "completed" | "inProgress" | "error";
  orders: number;
  carrier: string;
  weight: number;
  manifestGenerated: boolean;
}

interface OrdersViewProps {
  ordersData: OrderData[];
  batchesData: BatchData[];
  testId?: string;
}

export function OrdersView({
  ordersData,
  batchesData,
  testId,
}: OrdersViewProps) {
  const [selectedOrders, setSelectedOrders] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [selectedBatch, setSelectedBatch] = useState<string | null>(
    "BATCH-001",
  );
  const [activeTab, setActiveTab] = useState<string>("orders");
  const { profileType } = useRoasterProfile();
  const { t } = useTranslation();
  const isBCOGRoaster = profileType === "BC OG";

  return (
    <div
      data-testid={testId ?? setOrdersViewTestId("container")}
      className="space-y-6"
    >
      <CommonHeader
        title={t("views.orders.title")}
        description={t("views.orders.description")}
        actions={
          <>
            <Button variant="outline">
              <Download className="mr-2 h-4 w-4" />
              {t("views.orders.export")}
            </Button>
            {!isBCOGRoaster && (
              <>
                <Button className="bg-primary text-primary-foreground hover:bg-primary/90">
                  <Plus className="mr-2 h-4 w-4" />
                  {t("views.orders.newOrder")}
                </Button>
                <Button variant="outline">
                  <Filter className="mr-2 h-4 w-4" />
                  {t("common.filter")}
                </Button>
              </>
            )}
          </>
        }
      >
        <div className="flex items-center gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="search"
              placeholder={t("views.orders.searchPlaceholder")}
              className="pl-10"
            />
          </div>
        </div>
      </CommonHeader>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="orders">
            {t("views.orders.tabs.orders")}
          </TabsTrigger>
          <TabsTrigger value="batches" disabled={isBCOGRoaster}>
            {t("views.orders.tabs.batches")}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="orders" className="space-y-6 mt-6">
          <OrdersStats />
          {selectedOrders.length > 0 && !isBCOGRoaster && (
            <BatchActions
              selectedOrders={selectedOrders}
              onClearSelection={() => setSelectedOrders([])}
            />
          )}
          <OrdersTable
            data={ordersData}
            statusFilter={statusFilter}
            selectedOrders={selectedOrders}
            onSelectionChange={setSelectedOrders}
            disableSelection={isBCOGRoaster}
          />
        </TabsContent>

        <TabsContent value="batches" className="space-y-6 mt-6">
          {selectedBatch && <BatchSummary batchId={selectedBatch} />}
          <BatchesTable
            data={batchesData}
            selectedBatch={selectedBatch}
            onSelectBatch={setSelectedBatch}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
